import * as React from 'react';
import {
    Alert,
    Button,
    LoginForm,
    LoginMainFooterBandItem,
    LoginPage,
    Modal,
    ModalVariant,
    TextContent,
    TextVariants,
    Text
} from '@patternfly/react-core';
import logo from '@app/images/nda-logo-login.png';
import UserAuthentication from "@app/Security/UserAuthentication";
import { Link } from 'react-router-dom';
import ForgotForm from './ForgotForm';
import User from '@app/Interface/UserInterface';
import { useLocation } from "react-router-dom";
import htmlParser from '@app/utils/htmlParser';
import ResetPassword from './ResetPassword';
import User2FALoginForm from '@app/Components/User/User2FALoginForm';
import Cookies from 'js-cookie';
import RegistrationForm from '@app/Components/User/RegistrationForm';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';

// use explicit properties initialization to suppress warnings and errors in Typescript code.
interface AppLoginProps {
    handleLogin?: (user: User) => void;
}

const AppLogin: React.FC<AppLoginProps> = ({ handleLogin }) => {

    const location = useLocation();

    // checks to see if jwt was passed as a querystring
    const jwt = htmlParser.getParameterValueFromUrl("jwt", location.search);

    let userId = Number(htmlParser.getParameterValueFromUrl("id", location.search));

    if (isNaN(userId)) {
        userId = 0;
    }

    const [showHelperText, setShowHelperText] = React.useState(false);
    const [usernameValue, setUsernameValue] = React.useState("");
    const [isValidUsername, setIsValidUsername] = React.useState(true);
    const [passwordValue, setPasswordValue] = React.useState("");
    const [isValidPassword, setIsValidPassword] = React.useState(true);
    const [isRememberMeChecked, setIsRememberMeChecked] = React.useState(false);
    const [currentPath, setCurrentPath] = React.useState("");
    const [formTitle, setFormTitle] = React.useState("Log in to your Client Portal");
    const [viewSignupMessage, setViewSignupMessage] = React.useState(true);
    const [viewForgotCredentials, setViewForgotCredentials] = React.useState(true);
    const [viewValidateCodeForm, setViewValidateCodeForm] = React.useState(false);
    const [formIsProcessing, setFormIsProcessing] = React.useState(false);
    const [viewRegistrationForm, setViewRegistrationForm] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isUnconfirmed, setIsUnconfirmed] = React.useState(false);

    const handleModalToggle = () => {
        setIsModalOpen(!isModalOpen);
    }

    const handleReturnToLogin = () => {
        setShowHelperText(false);
        setViewValidateCodeForm(false);
        setViewRegistrationForm(false);
    }

    const handleUsernameChange = (value: React.SetStateAction<string>) => {
        setUsernameValue(value);
    }

    const handlePasswordChange = (value: React.SetStateAction<string>) => {
        setPasswordValue(value);
    }

    // TODO: remove completely? determine flow with/without 2FA?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onRememberMeClick = () => {
        setIsRememberMeChecked(!isRememberMeChecked);
    }

    const onLoginButtonClick = (event: { preventDefault: () => void; }) => {

        // prevent the default form submission
        event.preventDefault();

        setIsValidUsername(!!usernameValue);
        setIsValidPassword(!!passwordValue);
        setShowHelperText(!usernameValue || !passwordValue);

        if (!(!!usernameValue && !!passwordValue)) {
            return;
        }

        setFormIsProcessing(true);

        const twoFaExpireDate = Cookies.get('2fa-remember-device');

        UserAuthentication.signIn(usernameValue, passwordValue, true, twoFaExpireDate)
            .then(user => {

                setShowHelperText(false);

                if (handleLogin && typeof user !== "boolean") {
                    handleLogin(user);
                } else {
                    console.warn("no handleLogin function was passed!");
                }

            })
            .catch(error => {

                const errorMsg = String(error);

                // thrown by  
                if (errorMsg.includes("2FA")) {

                    setViewValidateCodeForm(true);

                    setShowHelperText(true);

                } else if (errorMsg.includes("Unconfirmed")) {

                    setIsUnconfirmed(true);

                } else {

                    setShowHelperText(true);

                }


            })
            .finally(() => {

                setFormIsProcessing(false);

            });

    };

    const validateCodeForm = (
        <User2FALoginForm email={usernameValue} handleLogin={handleLogin} />
    );

    const registrationForm = (
        <RegistrationForm userId={userId} jwt={jwt} handleReturnToLogin={handleReturnToLogin} />
    );

    const loginForm = (
        <LoginForm
            usernameLabel="Email"
            usernameValue={usernameValue}
            onChangeUsername={handleUsernameChange}
            isValidUsername={isValidUsername}
            passwordLabel="Password"
            passwordValue={passwordValue}
            onChangePassword={handlePasswordChange}
            isValidPassword={isValidPassword}
            onLoginButtonClick={onLoginButtonClick}
            isLoginButtonDisabled={formIsProcessing}
            loginButtonLabel={formIsProcessing ? "Logging In..." : "Log In"}
        />
    );

    const forgotForm = (
        <ForgotForm></ForgotForm>
    );

    const helperText = (
        <Alert variant="danger" title="Invalid login credentials." role="alert">
            <Text component={TextVariants.p}>
                The email or password entered is incorrect. Please try again or reset your password by clicking the link below.
                If you continue to have problems, please contact the NDA office staff at <a href="tel:1-877-632-6455">1-877-NDA-MILK</a> (<a href="tel:1-877-632-6455">1-877-632-6455</a>).
            </Text>
        </Alert>
    );

    const unconfirmedText = (
        <Alert variant="danger" title="Incomplete registration." role="alert">
            <Text component={TextVariants.p}>
                Your account registration has not been completed. Please check your email for information
                on how to complete your registration. If you continue to have problems, please contact the
                NDA office staff at <a href="tel:1-877-632-6455">1-877-NDA-MILK</a> (<a href="tel:1-877-632-6455">1-877-632-6455</a>).
            </Text>
        </Alert>
    );

    const newSiteMessage = (
        <Alert variant="info" title="Welcome" role="alert">
            <Text component={TextVariants.p} className="pf-u-mb-sm">
                Thank you for visiting the NDA Member and Northwest Laboratories Website.
                If you are having issues logging into the site,
                please contact the NDA office at <a href="tel:1-877-632-6455">1-877-632-6455</a>.
            </Text>
        </Alert>
    );

    const signUpForAccountMessage = (
        <LoginMainFooterBandItem>
            Need an account?&nbsp;
            <Button variant="link" onClick={handleModalToggle} className="pf-u-p-0">Request Access</Button>
            <Modal
                variant={ModalVariant.medium}
                title="New Account Registration"
                isOpen={isModalOpen}
                onClose={handleModalToggle}
                actions={[
                    <Button key="ok" variant="primary" onClick={handleModalToggle}>
                        OK
                    </Button>
                ]}>
                <TextContent>
                    <Text component={TextVariants.p}>Please call NDA staff at 1-877-NDA-MILK (1-877-632-6455) for assistance.</Text>
                </TextContent>
            </Modal>
        </LoginMainFooterBandItem>
    );

    const forgotCredentials = (
        <LoginMainFooterBandItem>
            <Link to="/forgot">Forgot password?</Link>
        </LoginMainFooterBandItem>
    );

    const returnToLogin = (
        <LoginMainFooterBandItem>
            <Link to="" onClick={handleReturnToLogin}>Return to login</Link>
        </LoginMainFooterBandItem>
    );

    React.useEffect(() => {

        let title = "Log in to your Client Portal";

        let blnViewSignup = true;

        let blnViewForgot = true;

        if (location.pathname === "/registration" && jwt && userId) {
            title = "Registration Form";
            blnViewSignup = false;
            setViewRegistrationForm(true);
        } else if (location.pathname === "/" && jwt) {
            title = "Reset Password";
            blnViewSignup = false;
        } else if (location.pathname === "/forgot") {
            title = "Forgot Password?";
            blnViewSignup = false;
            blnViewForgot = false;
        } else if (viewValidateCodeForm) {
            title = "Validate Login";
        }

        setCurrentPath(location.pathname);
        setFormTitle(title);
        setViewSignupMessage(blnViewSignup);
        setViewForgotCredentials(blnViewForgot);

    }, [location.pathname, jwt, viewValidateCodeForm, userId]);

    if (viewRegistrationForm) {

        return (
            <ToastNotifications>
                <LoginPage
                    brandImgSrc={logo}
                    loginTitle={formTitle}
                    signUpForAccountMessage={signUpForAccountMessage}
                    forgotCredentials={returnToLogin}
                >
                    {registrationForm}
                </LoginPage>
            </ToastNotifications>
        );

    }

    if (viewValidateCodeForm) {

        // 2FA validation code form
        return (
            <LoginPage
                brandImgSrc={logo}
                loginTitle={formTitle}
                signUpForAccountMessage={signUpForAccountMessage}
                forgotCredentials={returnToLogin}
            >
                {validateCodeForm}
            </LoginPage>
        );
    }

    return (
        <LoginPage
            brandImgSrc={logo}
            loginTitle={formTitle}
            signUpForAccountMessage={viewSignupMessage ? signUpForAccountMessage : null}
            forgotCredentials={viewForgotCredentials ? forgotCredentials : null}
        >
            {showHelperText && helperText}
            {newSiteMessage}
            {isUnconfirmed && unconfirmedText}
            {currentPath === "/forgot" && forgotForm}
            {currentPath === "/" && jwt && <ResetPassword jwt={jwt} userId={userId} />}
            {currentPath !== "/forgot" && !jwt && loginForm}
        </LoginPage>
    );

}

export default AppLogin;